/**
 * Analytics view readers (plan 09, BA-01/02/03). PostgREST reads of the
 * tenant-isolated (security_invoker RLS) analytics views — no Edge Fn needed for
 * the P0 income/expense/P&L reports.
 */
import { ApiError } from '@gym/shared';

import type { GymSupabaseClient } from '../supabase.js';

export interface ProfitLossRow {
  month_bucket: string;
  income: number;
  expense: number;
  profit: number;
}

export async function getProfitLoss(
  client: GymSupabaseClient,
  tenantId: string,
): Promise<ProfitLossRow[]> {
  const { data, error } = await client
    .from('v_profit_loss')
    .select('month_bucket, income, expense, profit')
    .eq('tenant_id', tenantId)
    .order('month_bucket', { ascending: false });
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []) as unknown as ProfitLossRow[];
}

export interface RevenueByKindRow {
  month_bucket: string;
  kind: string;
  total: number;
}

export async function getRevenueByKind(
  client: GymSupabaseClient,
  tenantId: string,
): Promise<RevenueByKindRow[]> {
  const { data, error } = await client
    .from('v_revenue_by_kind')
    .select('month_bucket, kind, total')
    .eq('tenant_id', tenantId)
    .order('month_bucket', { ascending: false });
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []) as unknown as RevenueByKindRow[];
}

export interface MembershipAnalyticsRow {
  status: string;
  member_count: number;
  expiring_30d: number;
}

export async function getMembershipAnalytics(
  client: GymSupabaseClient,
  tenantId: string,
): Promise<MembershipAnalyticsRow[]> {
  const { data, error } = await client
    .from('v_membership_analytics')
    .select('status, member_count, expiring_30d')
    .eq('tenant_id', tenantId);
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []) as unknown as MembershipAnalyticsRow[];
}

export interface RevenueRow {
  payment_id: string;
  amount: number;
  kind: string;
  method: string;
  fiscal_status: string;
  paid_at: string;
  membership_id: string | null;
  sale_id: string | null;
  month_bucket: string;
}

export interface RevenueFilters {
  kind?: string;
  fiscalStatus?: string;
  from?: string;
  to?: string;
}

export async function getRevenue(
  client: GymSupabaseClient,
  tenantId: string,
  filters: RevenueFilters = {},
): Promise<RevenueRow[]> {
  let q = client
    .from('v_revenue')
    .select(
      'payment_id, amount, kind, method, fiscal_status, paid_at, membership_id, sale_id, month_bucket',
    )
    .eq('tenant_id', tenantId);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (filters.kind) q = q.eq('kind', filters.kind as any);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (filters.fiscalStatus) q = q.eq('fiscal_status', filters.fiscalStatus as any);
  if (filters.from) q = q.gte('paid_at', filters.from);
  if (filters.to) q = q.lte('paid_at', filters.to);
  const { data, error } = await q.order('paid_at', { ascending: false });
  if (error) throw new ApiError('internal_error', { message: error.message, cause: error });
  return (data ?? []) as unknown as RevenueRow[];
}
